import {
  Card,
  CardContent,
  Button,
  EmptyState,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Select,
  Spin,
  useToast,
  Brand,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  AccordianCustom,
  type BreadcrumbItem,
} from "@src/presentation/components/common";
import { PageContent } from "@src/presentation/components/Layout/common";
import {
  UserDetailsBrandPermissionsContent,
  UserDetailsHeader,
  UserDetailsSummaryCard,
  UsersAddEditForm,
  AddBrandPermissionRow,
  UserBrandAccessStatusUpdate,
  type RoleOption,
  type UserAddEditFormValues,
} from "./utils";
import { useUsersAccessData } from "@src/presentation/providers/UsersAccessDataProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { useRolesPermissions } from "@src/presentation/providers/RolesPermissionsProvider";
import type { FeatureLookupItem } from "@src/domain/dto/features";
import type {
  UserDetailsBrand,
  UserDetailsFeature,
} from "@src/domain/entities/auth/user/UserDetails";
import { useUpdateUser } from "./utils/useUpdateUser";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";

function getCustomPermissionIds(
  permissions: UserDetailsFeature["permissions"] | undefined,
): Set<number> {
  const ids = new Set<number>();
  if (!permissions?.length) return ids;
  for (const permission of permissions) {
    if (permission.isCustom) {
      ids.add(permission.permissionId);
    }
  }
  return ids;
}

function initializeCustomPermissionState(
  brands: UserDetailsBrand[],
): Record<number, Record<number, Set<number>>> {
  const next: Record<number, Record<number, Set<number>>> = {};
  for (const brand of brands) {
    next[brand.brandId] = {};
    for (const feature of brand.features ?? []) {
      next[brand.brandId][feature.featureId] = getCustomPermissionIds(
        feature.permissions,
      );
    }
  }
  return next;
}

function initializeRoleSelectionState(
  brands: UserDetailsBrand[],
): Record<number, number | null> {
  const next: Record<number, number | null> = {};
  for (const brand of brands) {
    next[brand.brandId] = getBrandDefaultRoleId(brand);
  }
  return next;
}

function getBrandDefaultRoleId(brand: UserDetailsBrand): number | null {
  return brand.roles?.[0]?.roleId ?? null;
}

function getBrandRoleSelectValue(
  brand: UserDetailsBrand,
  roleId: number | null,
  roleOptions: RoleOption[],
): RoleOption | null {
  if (roleId == null) return null;

  const fromOptions = roleOptions.find(
    (option) => Number(option.value) === roleId,
  );
  if (fromOptions) return fromOptions;

  const roleFromBrand = brand.roles?.find((role) => role.roleId === roleId);
  if (roleFromBrand) {
    return {
      label: roleFromBrand.roleName,
      value: String(roleFromBrand.roleId),
    };
  }

  return null;
}

function flattenBrandCustomPermissions(
  customPermissionIdsByFeature: Record<number, Set<number>>,
): number[] {
  const permissionIds = new Set<number>();
  for (const featureIdKey of Object.keys(customPermissionIdsByFeature)) {
    const featureId = Number(featureIdKey);
    const featureSet = customPermissionIdsByFeature[featureId];
    if (!featureSet) continue;
    for (const permissionId of featureSet) {
      permissionIds.add(permissionId);
    }
  }
  return Array.from(permissionIds);
}

function clearBrandCustomPermissionsByFeature(
  currentBrandByFeature: Record<number, Set<number>> | undefined,
): Record<number, Set<number>> {
  const cleared: Record<number, Set<number>> = {};
  for (const featureIdKey of Object.keys(currentBrandByFeature ?? {})) {
    const featureId = Number(featureIdKey);
    cleared[featureId] = new Set<number>();
  }
  return cleared;
}

function mapCustomPermissionsToFeatureState(
  brand: UserDetailsBrand,
  customPermissionIds: Set<number>,
  featuresLookup: FeatureLookupItem[] | null,
): Record<number, Set<number>> {
  const next: Record<number, Set<number>> = {};
  const permissionToFeatureId = new Map<number, number>();

  for (const feature of featuresLookup ?? []) {
    next[feature.featureId] = new Set<number>();
    for (const permission of feature.featurePermissions ?? []) {
      permissionToFeatureId.set(permission.id, feature.featureId);
    }
  }

  for (const feature of brand.features ?? []) {
    if (!next[feature.featureId]) {
      next[feature.featureId] = new Set<number>();
    }
    for (const permission of feature.permissions ?? []) {
      permissionToFeatureId.set(permission.permissionId, feature.featureId);
    }
  }

  for (const permissionId of customPermissionIds) {
    const featureId = permissionToFeatureId.get(permissionId);
    if (featureId == null) continue;
    if (!next[featureId]) {
      next[featureId] = new Set<number>();
    }
    next[featureId].add(permissionId);
  }
  return next;
}

function areSetsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

export const UserDetails = () => {
  const { userId } = useParams<{ userId: string }>();
  const {
    userDetails,
    userDetailsLoading,
    getUserDetails,
    updateUserDetails,
    applyUserUpdate,
    updateUserBrand,
    updateUserBrandLoading,
    applyUserBrandAdd,
  } = useUsersAccessData();
  const parsedUserId = useMemo(() => {
    const parsed = Number(userId);
    return Number.isNaN(parsed) ? null : parsed;
  }, [userId]);

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const detailsName = userDetails?.fullName?.trim();
    return [
      {
        key: "users-root",
        title: "Users & Access",
        path: "/users-access",
      },
      {
        key: "user-current",
        title: detailsName || "User Details",
      },
    ];
  }, [userDetails?.fullName]);

  const {
    featuresLookup,
    organizationsBrandsLookup,
    organizationsBrandsLookupLoading,
  } =
    useGlobalDataLookup();
  const { rolePermissions, rolePermissionsLoading } = useRolesPermissions();
  const { error: errorToast, success: successToast } = useToast();
  const updateUser = useUpdateUser();
  const [selectedRoleByBrandId, setSelectedRoleByBrandId] = useState<
    Record<number, number | null>
  >({});
  const [initialRoleByBrandId, setInitialRoleByBrandId] = useState<
    Record<number, number | null>
  >({});
  const [
    customPermissionIdsByBrandFeature,
    setCustomPermissionIdsByBrandFeature,
  ] = useState<Record<number, Record<number, Set<number>>>>({});
  const [
    initialCustomPermissionIdsByBrandFeature,
    setInitialCustomPermissionIdsByBrandFeature,
  ] = useState<Record<number, Record<number, Set<number>>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isAddBrandRowVisible, setIsAddBrandRowVisible] = useState(false);

  const roleOptions = useMemo<RoleOption[]>(
    () =>
      (rolePermissions ?? []).map((role) => ({
        label: role.roleName,
        value: String(role.roleId),
      })),
    [rolePermissions],
  );

  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isSavingUserInfo, setIsSavingUserInfo] = useState(false);
  const [isUserFormSubmittable, setIsUserFormSubmittable] = useState(false);
  const [userForm] = Form.useForm<UserAddEditFormValues>();

  const editUserInitialValues = useMemo<UserAddEditFormValues>(
    () => ({
      firstName: userDetails?.firstName ?? "",
      lastName: userDetails?.lastName ?? "",
      email: userDetails?.email ?? "",
      isActive: userDetails?.isActive ?? true,
    }),
    [userDetails],
  );

  const closeEditUserModal = useCallback(() => {
    setIsEditUserModalOpen(false);
    setIsUserFormSubmittable(false);
    userForm.resetFields();
    userForm.setFieldsValue(editUserInitialValues);
  }, [editUserInitialValues, userForm]);

  const openEditUserModal = useCallback(() => {
    userForm.resetFields();
    userForm.setFieldsValue(editUserInitialValues);
    setIsUserFormSubmittable(false);
    setIsEditUserModalOpen(true);
  }, [editUserInitialValues, userForm]);

  const handleEditUserSubmit = async (values: UserAddEditFormValues) => {
    if (!parsedUserId) return;

    const authId = userDetails?.authId?.trim();
    if (!authId) {
      errorToast({
        content: "Cannot update user: authentication identity is missing.",
      });
      return;
    }

    setIsSavingUserInfo(true);
    try {
      const saved = await updateUser({
        userId: parsedUserId,
        authId,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: userDetails?.email?.trim() ?? values.email.trim(),
        isActive: Boolean(values.isActive),
      });

      applyUserUpdate(saved);
      successToast({ content: "User updated successfully." });
      closeEditUserModal();
    } catch (err) {
      console.error("Failed to update user", err);
      errorToast({
        content: "Failed to update user. Please try again.",
      });
    } finally {
      setIsSavingUserInfo(false);
    }
  };

  useEffect(() => {
    if (!parsedUserId) return;
    getUserDetails(parsedUserId);
  }, [getUserDetails, parsedUserId]);

  const brands = userDetails?.brands ?? [];

  const assignedBrandIds = useMemo(
    () => new Set(brands.map((brand) => brand.brandId)),
    [brands],
  );
  const brandLogoUrlByBrandId = useMemo(() => {
    const next = new Map<number, string | null>();
    for (const organization of organizationsBrandsLookup ?? []) {
      for (const brand of organization.brands ?? []) {
        next.set(brand.brandId, brand.brandLogoUrl ?? null);
      }
    }
    return next;
  }, [organizationsBrandsLookup]);

  useEffect(() => {
    if (!brands.length) return;
    const nextRoleState = initializeRoleSelectionState(brands);
    setSelectedRoleByBrandId(nextRoleState);
    setInitialRoleByBrandId(nextRoleState);
  }, [brands]);

  useEffect(() => {
    if (!brands.length) return;
    const nextCustomPermissionState = initializeCustomPermissionState(brands);
    setCustomPermissionIdsByBrandFeature(nextCustomPermissionState);
    setInitialCustomPermissionIdsByBrandFeature(nextCustomPermissionState);
  }, [brands]);

  const getRoleIdFromSelectValue = (value: unknown): number | null => {
    if (typeof value === "string" || typeof value === "number") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (
      value &&
      typeof value === "object" &&
      "value" in value &&
      (typeof value.value === "string" || typeof value.value === "number")
    ) {
      const parsed = Number(value.value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const handleBrandRoleChange = (brandId: number, value: unknown) => {
    const nextRoleId = getRoleIdFromSelectValue(value);
    setSelectedRoleByBrandId((prev) => ({
      ...prev,
      [brandId]: nextRoleId,
    }));
    setCustomPermissionIdsByBrandFeature((prev) => ({
      ...prev,
      [brandId]: clearBrandCustomPermissionsByFeature(prev[brandId]),
    }));
  };

  const toggleCustomPermission = (
    brandId: number,
    featureId: number,
    permissionId: number,
  ) => {
    setCustomPermissionIdsByBrandFeature((prev) => {
      const currentBrand = prev[brandId] ?? {};
      const currentFeatureSet = currentBrand[featureId] ?? new Set<number>();
      const nextFeatureSet = new Set(currentFeatureSet);
      if (nextFeatureSet.has(permissionId)) {
        nextFeatureSet.delete(permissionId);
      } else {
        nextFeatureSet.add(permissionId);
      }
      return {
        ...prev,
        [brandId]: {
          ...currentBrand,
          [featureId]: nextFeatureSet,
        },
      };
    });
  };

  const activebrandCount = useMemo(() => {
    return brands.filter((brand) => brand.isAccessActive).length;
  }, [brands]);
  const dirtyBrandIds = useMemo(() => {
    const changedBrandIds = new Set<number>();
    for (const brand of brands) {
      const brandId = brand.brandId;
      const selectedRoleId = selectedRoleByBrandId[brandId] ?? null;
      const initialRoleId = initialRoleByBrandId[brandId] ?? null;
      if (selectedRoleId !== initialRoleId) {
        changedBrandIds.add(brandId);
        continue;
      }

      const currentByFeature = customPermissionIdsByBrandFeature[brandId] ?? {};
      const initialByFeature =
        initialCustomPermissionIdsByBrandFeature[brandId] ?? {};
      const featureIds = new Set<number>([
        ...Object.keys(currentByFeature).map(Number),
        ...Object.keys(initialByFeature).map(Number),
      ]);

      for (const featureId of featureIds) {
        const currentPermissions =
          currentByFeature[featureId] ?? new Set<number>();
        const initialPermissions =
          initialByFeature[featureId] ?? new Set<number>();
        if (!areSetsEqual(currentPermissions, initialPermissions)) {
          changedBrandIds.add(brandId);
          break;
        }
      }
    }
    return Array.from(changedBrandIds);
  }, [
    brands,
    selectedRoleByBrandId,
    initialRoleByBrandId,
    customPermissionIdsByBrandFeature,
    initialCustomPermissionIdsByBrandFeature,
  ]);
  const hasUnsavedChanges = dirtyBrandIds.length > 0;

  const accordionBrands = useMemo(() => brands, [brands]);
  const isDetailsPending = userDetailsLoading || !userDetails;
  const handleSaveChanges = async () => {
    if (!dirtyBrandIds.length || isSaving) return;

    const payloadBrands = dirtyBrandIds
      .map((brandId) => {
        const brand = brands.find((item) => item.brandId === brandId);
        if (!brand) return null;
        const roleId =
          selectedRoleByBrandId[brandId] ?? getBrandDefaultRoleId(brand);
        if (roleId == null) return null;
        const customPermissions = flattenBrandCustomPermissions(
          customPermissionIdsByBrandFeature[brandId] ?? {},
        );
        return {
          brandUserId: brand.brandUserMappingId,
          roleId,
          customPermissions,
        };
      })
      .filter((brand): brand is NonNullable<typeof brand> => Boolean(brand));

    if (!payloadBrands.length) return;

    const payload = {
      brands: payloadBrands,
    };
    console.log("updateUserDetails payload", payload);

    setIsSaving(true);
    try {
      const responseItems = (await updateUserDetails(payload)) ?? [];
      const hasResponseItems = responseItems.length > 0;

      const roleUpdatesFromResponse = new Map<number, number>();
      const customPermissionsByBrandFromResponse = new Map<
        number,
        Record<number, Set<number>>
      >();
      if (hasResponseItems) {
        for (const item of responseItems) {
          roleUpdatesFromResponse.set(item.brandId, item.role.roleId);
          const brand = brands.find(
            (candidate) => candidate.brandId === item.brandId,
          );
          if (!brand) continue;
          const customIds = new Set<number>(
            (item.customPermissions ?? []).map(
              (permission) => permission.permissionId,
            ),
          );
          customPermissionsByBrandFromResponse.set(
            item.brandId,
            mapCustomPermissionsToFeatureState(
              brand,
              customIds,
              featuresLookup,
            ),
          );
        }
      }

      setSelectedRoleByBrandId((prev) => {
        const next = { ...prev };
        for (const brandId of dirtyBrandIds) {
          const roleIdFromResponse = roleUpdatesFromResponse.get(brandId);
          if (roleIdFromResponse != null) {
            next[brandId] = roleIdFromResponse;
          }
        }
        return next;
      });

      setCustomPermissionIdsByBrandFeature((prev) => {
        const next = { ...prev };
        for (const brandId of dirtyBrandIds) {
          const permissionsFromResponse =
            customPermissionsByBrandFromResponse.get(brandId);
          if (permissionsFromResponse) {
            next[brandId] = permissionsFromResponse;
          }
        }
        return next;
      });

      setInitialRoleByBrandId((prev) => {
        const next = { ...prev };
        for (const brandId of dirtyBrandIds) {
          const roleIdFromResponse = roleUpdatesFromResponse.get(brandId);
          if (roleIdFromResponse != null) {
            next[brandId] = roleIdFromResponse;
            continue;
          }
          next[brandId] = selectedRoleByBrandId[brandId] ?? null;
        }
        return next;
      });
      setInitialCustomPermissionIdsByBrandFeature((prev) => {
        const next = { ...prev };
        for (const brandId of dirtyBrandIds) {
          const permissionsFromResponse =
            customPermissionsByBrandFromResponse.get(brandId);
          if (permissionsFromResponse) {
            next[brandId] = permissionsFromResponse;
            continue;
          }
          next[brandId] = {};
          const currentByFeature =
            customPermissionIdsByBrandFeature[brandId] ?? {};
          for (const featureIdKey of Object.keys(currentByFeature)) {
            const featureId = Number(featureIdKey);
            next[brandId][featureId] = new Set(
              currentByFeature[featureId] ?? [],
            );
          }
        }
        return next;
      });

      successToast({
        content: "User role permissions updated",
        icon: <Icon name={Icons.CheckCircle} />,
      });
    } catch (error) {
      console.error("Failed to update user role permissions", error);
      errorToast({
        content: "Failed to update user role permissions. Please try again.",

        icon: <Icon name={Icons.AlertCircle} />,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddBrand = async (
    brandId: number,
    roleId: number,
    isActive: boolean,
  ) => {
    if (!parsedUserId || updateUserBrandLoading) return;

    try {
      const savedBrand = await updateUserBrand({
        userId: parsedUserId,
        brandId,
        roleId,
        isActive,
      });
      const resolvedLogoUrl =
        savedBrand.brandLogoUrl?.trim() ||
        brandLogoUrlByBrandId.get(savedBrand.brandId) ||
        null;
      applyUserBrandAdd({
        ...savedBrand,
        brandLogoUrl: resolvedLogoUrl,
      });
      setIsAddBrandRowVisible(false);
      successToast({
        content: "Brand assigned successfully.",
        icon: <Icon name={Icons.CheckCircle} />,
      });
    } catch (error) {
      console.error("Failed to assign brand to user", error);
      errorToast({
        content: "Failed to assign brand. Please try again.",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    }
  };

  let brandPermissionsContent;

  if (isDetailsPending) {
    brandPermissionsContent = (
      <Flex
        align="center"
        justifyContent="center"
        style={{ width: "100%", minHeight: "500px" }}
      >
        <Spin spinning tip="Loading..." />
      </Flex>
    );
  } else if (accordionBrands.length === 0 && !isAddBrandRowVisible) {
    brandPermissionsContent = (
      <EmptyState
        className="px-4 py-5 border-1 border-color-grey-25 bg-white"
        icon={Icons.InfoOctagon}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-2">
            No brands assigned
          </Heading>
        }
        description={
          <Paragraph className="text-grey-500 font-regular mb-0">
            This user does not have any brand assigned yet.
          </Paragraph>
        }
      />
    );
  } else {
    brandPermissionsContent = (
      <>
        {isAddBrandRowVisible ? (
          <AddBrandPermissionRow
            assignedBrandIds={assignedBrandIds}
            roleOptions={roleOptions}
            isSaving={updateUserBrandLoading}
            onCancel={() => setIsAddBrandRowVisible(false)}
            onSave={handleAddBrand}
          />
        ) : null}
        {accordionBrands.length > 0 ? (
          <AccordianCustom
            defaultExpandedKeys={accordionBrands.map((brand) =>
              String(brand.brandId),
            )}
            className="mb-2"
            items={accordionBrands.map((brand) => ({
              key: String(brand.brandId),
              title: (
                <Flex
                  align="center"
                  justifyContent="space-between"
                  style={{ flex: 1, paddingTop: "0.25rem" }}
                >
                  <Flex direction="row" gap={1}>
                    <Brand
                      size="medium"
                      variant="compact"
                      brandName={brand.brandName}
                      logoSrcCompact={brand.brandLogoUrl?.trim() || undefined}
                    />
                    <Flex direction="column">
                      <Heading
                        level={6}
                        className="m-0 text-grey-700 font-bold"
                      >
                        {brand.brandName}
                      </Heading>
                      <Paragraph className="m-0 text-grey-500">
                        {brand.organizationName}
                      </Paragraph>
                    </Flex>
                  </Flex>
                  <Flex align="center" gap={0.5} className="me-2">
                    <Paragraph className="m-0 text-grey-500">Role:</Paragraph>
                    <Select
                      key={`brand-role-select-${brand.brandId}-${getBrandDefaultRoleId(brand) ?? "none"}-${roleOptions.length}`}
                      size="medium"
                      isSearchable={false}
                      isClearable={false}
                      className="user-brand-role-select"
                      options={roleOptions}
                      defaultValue={
                        getBrandRoleSelectValue(
                          brand,
                          selectedRoleByBrandId[brand.brandId] ??
                            getBrandDefaultRoleId(brand),
                          roleOptions,
                        ) ?? undefined
                      }
                      onChange={(value: unknown) =>
                        handleBrandRoleChange(brand.brandId, value)
                      }
                      isLoading={rolePermissionsLoading}
                      isDisabled={rolePermissionsLoading}
                    />
                    <UserBrandAccessStatusUpdate brand={brand} />
                  </Flex>
                </Flex>
              ),
              content: (
                <Flex direction="column" gap={0.5} className="px-2 pb-2">
                  <UserDetailsBrandPermissionsContent
                    brand={brand}
                    rolePermissions={rolePermissions}
                    featuresLookup={featuresLookup}
                    selectedRoleByBrandId={selectedRoleByBrandId}
                    customPermissionIdsByBrandFeature={
                      customPermissionIdsByBrandFeature
                    }
                    onToggleCustomPermission={toggleCustomPermission}
                  />
                </Flex>
              ),
            }))}
          />
        ) : null}
      </>
    );
  }

  return (
    <ErrorBoundary>
      <UserDetailsHeader
        breadcrumbItems={breadcrumbItems}
        title={userDetails?.fullName?.trim() || "User Details"}
        onEditUserClick={openEditUserModal}
        isEditUserDisabled={!userDetails || userDetailsLoading || !parsedUserId}
        onSaveChanges={handleSaveChanges}
        isSaveDisabled={!hasUnsavedChanges || isSaving}
        isSaveLoading={isSaving}
      />

      <Modal
        isOpen={isEditUserModalOpen}
        onClose={closeEditUserModal}
        size="large"
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Edit User
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              size="small"
              onPress={closeEditUserModal}
              color="grey"
              isDisabled={isSavingUserInfo}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => userForm.submit()}
              isLoading={isSavingUserInfo}
              isDisabled={isSavingUserInfo || !isUserFormSubmittable}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <UsersAddEditForm
          form={userForm}
          mode="edit"
          initialValues={editUserInitialValues}
          onSubmit={handleEditUserSubmit}
          onFormValidityChange={setIsUserFormSubmittable}
        />
      </Modal>

      <PageContent className="p-3">
        <UserDetailsSummaryCard
          userDetailsLoading={userDetailsLoading}
          userDetails={userDetails}
          activebrandCount={activebrandCount}
        />

        <Flex align="center" justifyContent="space-between" className="mb-2">
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Brand Permissions
          </Heading>
          <HasAnyPermission
            requiredPermissions={[Permissions.USER_MANAGEMENT_EDIT]}
          >
            <Button
              size="small"
              color="primary"
              onPress={() => setIsAddBrandRowVisible(true)}
              isDisabled={
                isDetailsPending ||
                isAddBrandRowVisible ||
                !parsedUserId ||
                organizationsBrandsLookupLoading
              }
            >
              <Flex align="center" gap={0.25}>
                <Icon name={Icons.Plus} size="small" />
                Add Brand
              </Flex>
            </Button>
          </HasAnyPermission>
        </Flex>

        <Card className="mb-3" variant="flat">
          <CardContent className="p-0">{brandPermissionsContent}</CardContent>
        </Card>
      </PageContent>
    </ErrorBoundary>
  );
};
